import { useEffect, useRef, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import * as signalR from '@microsoft/signalr';
import { hubConnectionOptions } from '../utils/hubConnectionOptions';
import { ChatMessage, ConnectionState } from '../types/chat';
import { LiveCohostInfo } from '../types/auth';
import { API_BASE_URL, LIVE_CHAT_CONFIG } from '../constants/chat';
import { chatSendWaitMs } from '../utils/chatSendBudget';
import { analytics } from '../services/analytics';
import { normalizeHubError } from '../utils/hubError';
import { ERROR_CODES, errorCodeMessage } from '../services/errorCodes';
import { resolveApiUrl, ruFallbackUrl, markApiRuSticky } from '../services/apiFallback';
import { resolveHubUrl } from '../services/apiEndpoint';
import { startHubWithTimeout } from '../utils/hubStartWithTimeout';
import { appendLiveChatMessage, mergeLiveChatHistory } from '../utils/liveChatHistoryMerge';
import { loadLiveChatHistoryPages } from '../utils/liveChatHistoryPages';
import { isAgapellaSubscriptionRequired } from '../services/agapellaGate';
import { useAgapellaAccess } from '../services/agapellaAccess';
import { api } from '../services/api';

// CORE-1612 — отказ по частоте человеку говорим по-русски, тем же текстом, что
// и остальному приложению.
const RATE_LIMITED_TEXT = errorCodeMessage(ERROR_CODES.RateLimited) ?? 'Слишком часто. Подождите немного и попробуйте снова.';

/** Сервер эфира шлёт этот отказ голой английской строкой, без кода. */
export const liveChatHubErrorText = (payload: unknown): string =>
  typeof payload === 'string' && /too fast/i.test(payload) ? RATE_LIMITED_TEXT : normalizeHubError(payload);

const { sendBurst: SEND_BURST, sendWindowMs: SEND_WINDOW_MS, maxMessageLength: MAX_MESSAGE_LENGTH, maxMessages: MAX_MESSAGES, historyMessages: HISTORY_MESSAGES, historyPageSize: HISTORY_PAGE_SIZE } = LIVE_CHAT_CONFIG;

// CORE-1253 — веб держит всю историю эфира, которую догружает по REST.
const KEEP_MESSAGES = Platform.OS === 'web' ? HISTORY_MESSAGES : MAX_MESSAGES;

// CORE-407 — how long to wait for the direct live-chat hub socket before
// falling back to the api-ru edge. RU ISPs that block the hub let TCP connect
// but hang the WS, so a timeout (not an error) is what triggers the fallback.
// Mirrors ChatApiChannel's HUB_CONNECT_TIMEOUT_MS.
const LIVE_HUB_CONNECT_TIMEOUT_MS = 10_000;

/** conn.start() but reject after `ms` — a hung RU WS never rejects on its own,
 *  so without this the viewer sits on "connecting" forever.
 *
 *  CORE-1202 — сама гонка переехала в `startHubWithTimeout`: местная копия
 *  бросала проигравший `conn.start()` без подписчика, и его отмена нашим же
 *  `stop()` уходила в `onunhandledrejection` («Server returned handshake
 *  error: Handshake was canceled», прод, комната 207). Здесь остаётся только
 *  адрес для этого отказа. */
function startWithTimeout(
  conn: signalR.HubConnection,
  ms: number,
  liveId: number | string,
): Promise<void> {
  return startHubWithTimeout(conn, ms, 'live chat hub', (err) => {
    analytics.track('live_chat_hub_start_aborted', {
      liveId,
      reason: err instanceof Error ? err.message : String(err),
    });
  });
}

/** CORE-528/846 — a no-account listener, as the эфир hub reports them. */
export interface LiveSpectator {
  spectatorId: string;
  name: string;
}

export interface UseLiveChatReturn {
  messages: ChatMessage[];
  /**
   * CORE-1253 — история этого эфира уже получена. До этого пустой `messages`
   * значит «ещё не знаем», а не «сообщений нет», и звать человека написать
   * первым рано: поздно вошедший увидел бы пустой чат поверх чужой переписки.
   */
  historyReady: boolean;
  viewerCount: number;
  /**
   * CORE-1201 — кто сейчас на сцене эфира, с состоянием модерации каждого.
   * `JoinLiveChat` кладёт соединение в ту же группу `live-chat-{id}`, в которую
   * бэкенд рассылает `CohostsChanged`, так что ростер приезжает сюда без
   * второго сокета. `null` = ни одной рассылки ещё не было — это НЕ пустая
   * сцена, и трактовать его как запрет нельзя (см. `resolveStageModeration`).
   */
  stageRoster: LiveCohostInfo[] | null;
  /** Named no-account listeners witnessed on this connection. */
  spectators: LiveSpectator[];
  /** How many the server holds in total — named rows plus those we joined too late to witness. */
  spectatorCount: number;
  connectionState: ConnectionState;
  error: string | null;
  /**
   * CORE-1778 — хаб отказал по подписке Agapēlla. Это не сбой: плашка не
   * исчезает, соединение закрыто и само не переподключается. Откроется
   * доступ — чат войдёт заново.
   */
  subscriptionRequired: boolean;
  sendMessage: (text: string) => Promise<boolean>;
  canSend: boolean;
  retry: () => void;
}

/** CORE-1525 — коды доступа к Live «по приглашению», как у `api.getLiveById`. */
export interface LiveChatAccess {
  inviteCode?: string | null;
  previewToken?: string | null;
}

/**
 * CORE-528/846 — pass `spectator` to enter the live as a NAMED no-account
 * listener instead of an anonymous +1 on the viewer badge. The id must be the
 * stable one from `api.spectateLive`: the roster is keyed on it, so the same
 * guest overwrites their own row on reconnect rather than doubling.
 */
export function useLiveChat(
  liveId: number,
  authToken: string | null,
  spectator?: LiveSpectator | null,
  access?: LiveChatAccess | null,
): UseLiveChatReturn {
  const inviteCode = access?.inviteCode ?? null;
  const previewToken = access?.previewToken ?? null;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyReady, setHistoryReady] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [canSend, setCanSend] = useState(true);
  const [viewerCount, setViewerCount] = useState(0);
  const [spectators, setSpectators] = useState<LiveSpectator[]>([]);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [stageRoster, setStageRoster] = useState<LiveCohostInfo[] | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const agapellaAccess = useAgapellaAccess();

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  // Held in a ref, not a dep: the identity is stable per browser, but the
  // object around it is re-created on every render, and putting it in the dep
  // array would rebuild the whole hub connection each time.
  const spectatorRef = useRef<LiveSpectator | null>(spectator ?? null);
  spectatorRef.current = spectator ?? null;
  const sentTimesRef = useRef<number[]>([]);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // CORE-1253 — чей эфир лежит в `messages`. Список переживает пересборку
  // соединения (токен, retry, реконнект) и должен её переживать, но не смену
  // эфира: история сливается, а не заменяется, и без сброса чат прошлого
  // выпуска смешался бы с чатом нового.
  const messagesLiveIdRef = useRef(liveId);

  const showError = useCallback((msg: string) => {
    setError(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(null), 4000);
  }, []);

  useEffect(() => {
    let destroyed = false;
    if (messagesLiveIdRef.current !== liveId) {
      messagesLiveIdRef.current = liveId;
      setMessages([]);
      setHistoryReady(false);
    }
    // Reassigned to the api-ru connection if the direct hub socket times out
    // (RU fallback), so the cleanup below always stops the live connection.
    let connection: signalR.HubConnection | null = null;

    // CORE-1525 — Live «по приглашению» пускает в чат только с кодом: на голый
    // `JoinLiveChat` приглашённому приходит кадр Error «Live not found.» вместо
    // истории. Стенд без нового метода хаба — входим по-старому.
    const joinChat = (conn: signalR.HubConnection): Promise<void> => {
      if (!inviteCode && !previewToken) return conn.invoke('JoinLiveChat', liveId);
      return conn.invoke('JoinLiveChatWithAccess', liveId, inviteCode, previewToken)
        .catch((err: unknown) => {
          if (/Method does not exist/i.test(String((err as Error)?.message ?? err))) {
            return conn.invoke('JoinLiveChat', liveId);
          }
          throw err;
        });
    };

    // Build (but don't start) a hub connection to `baseUrl` with every event
    // handler wired. Split out so we can rebuild against the api-ru edge.
    const buildConnection = (baseUrl: string): signalR.HubConnection => {
      // CORE-1364 — token goes through accessTokenFactory, never the URL.
      const conn = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/hubs/chat`, hubConnectionOptions(authToken))
        .withAutomaticReconnect()
        .build();

      // CORE-1253 — сливаем, а не заменяем: см. `liveChatHistoryMerge`.
      conn.on('ChatHistory', (history: ChatMessage[]) => {
        if (destroyed) return;
        setMessages(prev => mergeLiveChatHistory(prev, history ?? [], KEEP_MESSAGES));
        setHistoryReady(true);
        // CORE-1253 — хаб отдаёт только последние 50 сообщений, а поздно
        // вошедший и читающий завершённую Live должны видеть историю с
        // начала. Догружаем её по REST после кадра хаба, то есть когда хаб
        // уже пустил в этот чат. Слияние то же, поэтому порядок прихода не
        // важен; REST не ответил — остаётся окно хаба. Пока веб.
        // CORE-1820 — REST отдаёт не больше 200 за раз, старше листаем назад.
        if (Platform.OS === 'web' && history?.length) {
          loadLiveChatHistoryPages(
            beforeId => api.getChatHistory(liveId, HISTORY_PAGE_SIZE, previewToken, inviteCode, beforeId),
            page => setMessages(prev => mergeLiveChatHistory(prev, page, KEEP_MESSAGES)),
            { pageSize: HISTORY_PAGE_SIZE, max: HISTORY_MESSAGES, isCancelled: () => destroyed },
          ).catch(() => { /* хватит уже пришедшего */ });
        }
      });

      conn.on('NewMessage', (message: ChatMessage) => {
        if (!destroyed) setMessages(prev => appendLiveChatMessage(prev, message, KEEP_MESSAGES));
      });

      // "Error" arrives as a plain string OR a { code, message } object
      // (kicked / moderation_blocked) — normalize so we never setError(object).
      conn.on('Error', (errorPayload: unknown) => {
        if (destroyed) return;
        // CORE-1778 — повторный вход получил бы тот же отказ: закрываемся.
        if (isAgapellaSubscriptionRequired(errorPayload)) {
          setSubscriptionRequired(true);
          conn.stop().catch(() => {});
          return;
        }
        showError(liveChatHubErrorText(errorPayload));
      });

      conn.on('ViewerCountUpdated', (data: { liveId: number; viewerCount: number }) => {
        if (!destroyed && data.liveId === liveId) {
          setViewerCount(data.viewerCount);
        }
      });

      // CORE-528 AC 3/8/9 — named listeners. Dedupe on spectatorId: the same
      // guest re-announces themselves after a reconnect, and without this the
      // master would watch one person pile up as several.
      conn.on('SpectatorJoined', (data: { liveId: number; spectatorId: string; name: string; spectatorCount: number }) => {
        if (destroyed || data.liveId !== liveId) return;
        setSpectators(prev => {
          const without = prev.filter(sp => sp.spectatorId !== data.spectatorId);
          return [...without, { spectatorId: data.spectatorId, name: data.name }];
        });
        setSpectatorCount(data.spectatorCount);
      });

      conn.on('SpectatorLeft', (data: { liveId: number; spectatorId: string; spectatorCount: number }) => {
        if (destroyed || data.liveId !== liveId) return;
        setSpectators(prev => prev.filter(sp => sp.spectatorId !== data.spectatorId));
        setSpectatorCount(data.spectatorCount);
      });

      // CORE-1201 — ростер сцены. Он же переносит запреты ведущего на микрофон и
      // камеру: состояние едет вместе со списком, поэтому реконнект, поздний
      // вход и перезагрузка получают его тем же путём, что и первый кадр.
      conn.on('CohostsChanged', (data: { liveId: number; cohosts: LiveCohostInfo[] }) => {
        if (destroyed || data.liveId !== liveId) return;
        setStageRoster(data.cohosts ?? []);
      });

      conn.onreconnecting(() => {
        if (!destroyed) setConnectionState('reconnecting');
      });

      conn.onreconnected(() => {
        if (!destroyed) {
          setConnectionState('connected');
          // Rejoin room after reconnect
          joinChat(conn).catch(() => {});
          // Re-announce ourselves as a listener too, otherwise the guest silently
          // vanishes from everyone else's roster after a network blip (AC 9).
          if (spectatorRef.current) {
            conn.invoke('SpectateLive', liveId, spectatorRef.current.spectatorId, spectatorRef.current.name)
              .catch(() => {});
          }
        }
      });

      conn.onclose(() => {
        if (!destroyed) setConnectionState('disconnected');
      });

      return conn;
    };

    // CORE-407 — connect to the direct hub with a timeout; if it hangs (RU ISP
    // block), rebuild against the api-ru edge and trip the shared sticky breaker
    // so subsequent REST + hubs skip the dead-direct attempt. Mirrors
    // ChatApiChannel.connect(). resolveApiUrl() honours an already-sticky breaker.
    const connectWithFallback = async (): Promise<void> => {
      // CORE-45: RU-first — when the startup probe picked the RU edge, start
      // there directly (cross-platform); resolveApiUrl still honours the web
      // reactive sticky breaker underneath.
      const primaryBase = resolveHubUrl(resolveApiUrl(API_BASE_URL));
      connection = buildConnection(primaryBase);
      connectionRef.current = connection;
      try {
        await startWithTimeout(connection, LIVE_HUB_CONNECT_TIMEOUT_MS, liveId);
      } catch (err) {
        const fallbackBase = ruFallbackUrl(API_BASE_URL);
        // No RU edge for this host (dev) or already on it — give up as before.
        if (destroyed || fallbackBase === primaryBase) throw err;
        try { await connection.stop(); } catch { /* abort the timed-out start */ }
        connection = buildConnection(fallbackBase);
        connectionRef.current = connection;
        await startWithTimeout(connection, LIVE_HUB_CONNECT_TIMEOUT_MS, liveId);
        markApiRuSticky();
      }
    };

    setConnectionState('connecting');
    setSubscriptionRequired(false);

    connectWithFallback()
      .then(() => {
        if (destroyed) { connection?.stop(); return; }
        setConnectionState('connected');
        return connection ? joinChat(connection) : undefined;
      })
      .then(() => {
        if (destroyed) return;
        // Хаб шлёт `ChatHistory` внутри `JoinLiveChat`, а кадры одного
        // соединения приходят по порядку: к ответу на вызов история уже здесь.
        // Если её не было вовсе, ждать больше нечего — чат действительно пуст.
        setHistoryReady(true);
        if (!spectatorRef.current) return;
        return connection?.invoke(
          'SpectateLive', liveId, spectatorRef.current.spectatorId, spectatorRef.current.name,
        );
      })
      .catch(() => {
        if (!destroyed) setConnectionState('disconnected');
      });

    return () => {
      destroyed = true;
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      if (spectatorRef.current) connection?.invoke('LeaveSpectate', liveId).catch(() => {});
      connection?.invoke('LeaveLiveChat', liveId).catch(() => {});
      connection?.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [liveId, authToken, inviteCode, previewToken, retryCount, showError]);

  // CORE-1612 — `false` значит «сообщение не ушло»: панель к этому моменту уже
  // очистила поле и по этому ответу возвращает написанное обратно.
  const sendMessage = useCallback(async (text: string): Promise<boolean> => {
    const trimmed = text.trim();
    if (!trimmed) {
      showError('Message cannot be empty.');
      return false;
    }
    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      showError(`Message cannot exceed ${MAX_MESSAGE_LENGTH} characters.`);
      return false;
    }

    if (chatSendWaitMs(sentTimesRef.current, Date.now(), SEND_BURST, SEND_WINDOW_MS) > 0) {
      analytics.track('chat_message_rate_limited', { event_id: liveId });
      showError(RATE_LIMITED_TEXT);
      return false;
    }

    const connection = connectionRef.current;
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      analytics.track('chat_message_failed', {
        event_id: liveId,
        reason: 'not_connected',
        connectionState: connection?.state ?? 'no_connection',
      });
      showError('Not connected to chat.');
      return false;
    }

    try {
      await connection.invoke('SendMessage', liveId, trimmed);
      analytics.track('chat_message_sent', { event_id: liveId, user_state: 'user' });
      sentTimesRef.current.push(Date.now());

      // CORE-1612 — кнопка гаснет, только когда серия исчерпана, и ровно до
      // момента, когда самое старое сообщение выйдет из окна.
      const wait = chatSendWaitMs(sentTimesRef.current, Date.now(), SEND_BURST, SEND_WINDOW_MS);
      if (wait > 0) {
        setCanSend(false);
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = setTimeout(() => setCanSend(true), wait);
      }
      return true;
    } catch (err: any) {
      analytics.track('chat_message_failed', {
        event_id: liveId,
        reason: 'invoke_threw',
        errorMessage: err?.message ?? String(err),
      });
      showError('Failed to send message.');
      return false;
    }
  }, [liveId, showError]);

  const retry = useCallback(() => {
    setRetryCount(c => c + 1);
  }, []);

  // CORE-1778 — доступ открылся (окно, оплата на другом устройстве и возврат
  // в приложение) — войти в чат заново.
  useEffect(() => {
    if (subscriptionRequired && agapellaAccess?.hasAccess === true) retry();
  }, [subscriptionRequired, agapellaAccess?.hasAccess, retry]);

  return { messages, historyReady, viewerCount, stageRoster, spectators, spectatorCount, connectionState, error, subscriptionRequired, sendMessage, canSend, retry };
}
